import { useMemo, useState } from "react";
import { runFinanceFxSource } from "../../../../api/financeFxSourceControls";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { useAuth } from "../../../../auth/useAuth";
import {
  formatCompletedTime,
  formatRelativeTime,
} from "../../intelligence/intelligenceLabels";
import DataSourceControlsGrid from "../dataSources/DataSourceControlsGrid";
import DataSourceScheduleModal from "../dataSources/DataSourceScheduleModal";
import { mapFinanceSourceToGridRow } from "../dataSources/mapDataSourceGridRow";
import { useOverlaySourceSchedule } from "../dataSources/useOverlaySourceSchedule";

function formatPurchasingPower(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return Number(value).toLocaleString("en-US", {
    maximumFractionDigits: value >= 100 ? 0 : 2,
  });
}

function SpotlightCell({ pairs }) {
  if (!pairs?.length) {
    return <span className="muted">—</span>;
  }

  return (
    <div className="admin-re-run-metrics-inline" style={{ flexWrap: "wrap", gap: "0.35rem" }}>
      {pairs.map((pair) => (
        <span
          key={pair.currency}
          className="muted admin-re-source-code"
          title={
            pair.present
              ? `1 ${pair.currency} ≈ ${formatPurchasingPower(pair.purchasingPowerCop)} COP`
              : `${pair.currency} missing from latest snapshot`
          }
        >
          {pair.currency}
          {pair.present ? " ✓" : " ✗"}
          {pair.present ? ` · ${formatPurchasingPower(pair.purchasingPowerCop)}` : ""}
        </span>
      ))}
    </div>
  );
}

function LatestSnapshotSummary({ source }) {
  if (source.snapshotError) {
    return <span className="muted">{source.snapshotError}</span>;
  }
  if (!source.fetchedAt && !source.snapshotId) {
    return <span className="muted">No snapshot yet</span>;
  }

  return (
    <div className="admin-re-latest-run">
      <div>
        {source.fetchedAt ? formatRelativeTime(source.fetchedAt) : "—"}
        {source.effectiveDate ? ` · as of ${source.effectiveDate}` : ""}
      </div>
      <div className="muted admin-re-source-code">
        {source.snapshotSource || "unknown source"}
        {source.snapshotId != null ? ` · #${source.snapshotId}` : ""}
      </div>
      {source.lastRun?.status ? (
        <div className="muted admin-re-source-code">
          Last run: {source.lastRun.status}
          {source.lastRun.reason ? ` (${source.lastRun.reason})` : ""}
          {source.lastRun.finishedAt
            ? ` · ${formatCompletedTime(source.lastRun.finishedAt)}`
            : ""}
        </div>
      ) : null}
      {source.failureStreak > 0 ? (
        <div className="muted admin-re-source-code">
          Failure streak: {source.failureStreak}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Single-row Finance FX controls for Midpoint Data Sources hub (M1+M4).
 */
export default function FinanceSourceControlsTable({
  sources,
  filters,
  onRefresh,
  showToast,
  setSources,
  hideEmpty = false,
}) {
  const [busyKey, setBusyKey] = useState(null);
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManageSources =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);
  const overlaySchedule = useOverlaySourceSchedule({
    domain: "finance",
    onRefresh,
    showToast,
    onScheduleSaved: (source, schedule) => {
      setSources?.((current) =>
        (current || []).map((row) =>
          row.sourceKey === source.sourceKey
            ? {
                ...row,
                scheduleEnabled: schedule.scheduleEnabled,
                scheduleIntervalMinutes: schedule.scheduleIntervalMinutes,
                nextRunAt: schedule.nextRunAt,
                supportsSchedule: true,
              }
            : row
        )
      );
    },
  });

  const filteredSources = useMemo(() => {
    let list = [...(sources || [])];
    if (filters?.search) {
      const query = filters.search.toLowerCase();
      list = list.filter(
        (source) =>
          source.sourceName?.toLowerCase().includes(query) ||
          source.sourceKey?.toLowerCase().includes(query) ||
          source.provider?.toLowerCase().includes(query)
      );
    }
    if (filters?.enabledFilter === "enabled") {
      list = list.filter((source) => source.enabled || source.isActive);
    } else if (filters?.enabledFilter === "disabled") {
      list = list.filter((source) => !(source.enabled || source.isActive));
    }
    if (filters?.statusFilter && filters.statusFilter !== "all") {
      list = list.filter((source) => {
        const status = String(source.health || source.healthStatus || "").toLowerCase();
        if (filters.statusFilter === "healthy") return status === "healthy";
        if (filters.statusFilter === "failed") return status === "failed";
        if (filters.statusFilter === "warning") {
          return status === "warning" || status === "stale";
        }
        if (filters.statusFilter === "never_run") return status === "never_run";
        if (filters.statusFilter === "disabled") return status === "disabled";
        return true;
      });
    }
    return list.map((source) => mapFinanceSourceToGridRow(source));
  }, [sources, filters]);

  async function handleRunNow(source) {
    if (!canManageSources || busyKey) return;
    setBusyKey(source.sourceKey);
    setSources?.((current) =>
      (current || []).map((row) =>
        row.sourceKey === source.sourceKey
          ? { ...row, isRunning: true, health: "running", healthStatus: "running" }
          : row
      )
    );

    try {
      const payload = await runFinanceFxSource(source.sourceKey);
      const nextSource = payload.source;
      if (nextSource) {
        setSources?.((current) =>
          (current || []).map((row) =>
            row.sourceKey === source.sourceKey ? { ...row, ...nextSource, isRunning: false } : row
          )
        );
      }
      const status = payload.refresh?.status;
      if (status === "success") {
        showToast?.("FX rates refreshed and snapshot saved.", "success");
      } else if (status === "skipped") {
        showToast?.(
          `FX refresh skipped: ${payload.refresh?.reason || "provider unavailable"}.`,
          "neutral"
        );
      } else {
        showToast?.(
          `FX refresh ${status || "failed"}: ${payload.refresh?.reason || "unknown error"}.`,
          "error"
        );
      }
      await onRefresh?.({ silent: true });
    } catch (error) {
      showToast?.(error.message || "Unable to refresh FX rates.", "error");
      setSources?.((current) =>
        (current || []).map((row) =>
          row.sourceKey === source.sourceKey ? { ...row, isRunning: false } : row
        )
      );
    } finally {
      setBusyKey(null);
    }
  }

  if (!filteredSources.length) {
    if (hideEmpty) {
      return null;
    }
    return <p className="muted">No Finance FX sources match the current filters.</p>;
  }

  return (
    <>
      <DataSourceControlsGrid
        sources={filteredSources}
        emptyMessage="No Finance FX sources match the current filters."
        busyKey={busyKey}
        canManageSources={canManageSources}
        onRunNow={handleRunNow}
        deleteDomain="finance"
        onDeleted={() => onRefresh?.()}
        onSaveSchedule={overlaySchedule.handleSaveSchedule}
        sectionLabel="Finance"
        onViewRuns={(source) =>
          showToast?.(
            source.snapshotId
              ? `Latest FX snapshot #${source.snapshotId} from ${source.snapshotSource || "unknown"}.`
              : "No FX snapshot yet.",
            "neutral"
          )
        }
        resolveRunNowTitle={(source) =>
          source.providerConfigured
            ? "Fetch live FX rates and persist snapshot"
            : "Run Now requires FX_PROVIDER + provider credentials; may skip if unset"
        }
        renderSourceExtras={(source) => (
          <>
            <LatestSnapshotSummary source={source} />
            <SpotlightCell pairs={source.spotlightCurrencies} />
          </>
        )}
      />
      <DataSourceScheduleModal
        source={overlaySchedule.scheduleSource}
        isOpen={Boolean(overlaySchedule.scheduleSource)}
        isSaving={overlaySchedule.scheduleSaving}
        onClose={() => overlaySchedule.setScheduleSource(null)}
        onSave={overlaySchedule.handleSaveSchedule}
      />
    </>
  );
}
